import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plane, CreditCard, Users, RefreshCw, ArrowLeftRight, Heart, 
  FileText, XCircle, ArrowLeft, ArrowRight, Clock, Check, Upload, User, Globe, Phone, Mail, Calendar, FileUp, Sparkles, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PriceDisplay } from '../components/PriceDisplay';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  Plane: Plane,
  CreditCard: CreditCard,
  Users: Users,
  RefreshCw: RefreshCw,
  RefreshCcw: RefreshCw, // Alias
  ArrowLeftRight: ArrowLeftRight,
  Heart: Heart,
  FileText: FileText,
  XCircle: XCircle,
  IdCard: CreditCard // Alias for ID
};

export default function VisaServices() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    passport: null,
    photo: null,
    emiratesId: null,
    other: null
  });
  const [formData, setFormData] = useState({
    service_type: 'visa',
    full_name_ar: '',
    full_name_en: '',
    nationality: '',
    passport_number: '',
    passport_expiry: '',
    current_visa_status: '',
    phone: '',
    email: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    sponsor_name: '',
    sponsor_uid: '',
    notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      setFormData(prev => ({
        ...prev,
        full_name_en: parsed.name || '',
        email: parsed.email || '',
        phone: parsed.phone || ''
      }));
    }
  }, []);

  // Fetch services from API
  useEffect(() => {
    fetchServices();
  }, [activeFilter]);

  const fetchServices = async () => {
    setServicesLoading(true);
    try {
      const url = activeFilter === 'all' 
        ? `${BACKEND_URL}/api/services/visa`
        : `${BACKEND_URL}/api/services/visa?service_type=${activeFilter}`;
      
      const response = await axios.get(url);
      if (response.data.success) {
        setServices(response.data.services);
        if (response.data.categories || response.data.service_types) {
          setCategories(response.data.service_types || response.data.categories);
        }
      }
    } catch (error) {
      console.error('Error fetching visa services:', error);
      toast.error(isAr ? 'خطأ في تحميل الخدمات' : 'Error loading services');
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch required documents when service is selected
  const fetchServiceDocuments = async (serviceId, nationality = null) => {
    setLoadingDocs(true);
    try {
      let url = `${BACKEND_URL}/api/services/visa/${serviceId}/documents`;
      if (nationality) {
        url += `?nationality=${nationality}`;
      }
      
      const response = await axios.get(url);
      if (response.data.success) {
        setRequiredDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching service documents:', error);
      setRequiredDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Get icon component for service
  const getServiceIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || CreditCard;
    return <IconComponent className="h-6 w-6" />;
  };

  // Transform service data for display
  const displayServices = services.map(service => {
    // Format price based on price_type
    let displayPrice = service.price;
    let priceRange = null;
    if (service.price_type === 'range') {
      priceRange = { min: service.price_min, max: service.price_max };
    }
    
    // Format duration based on duration_type
    let processingTime = isAr ? service.processing_time_ar : service.processing_time_en;
    if (!processingTime) {
      if (service.duration_type === 'range') {
        processingTime = isAr 
          ? `${service.duration_min}-${service.duration_max} أيام عمل`
          : `${service.duration_min}-${service.duration_max} Working Days`;
      } else {
        const days = service.duration_days || 1;
        processingTime = isAr 
          ? (days === 1 ? 'يوم عمل واحد' : `${days} أيام عمل`)
          : (days === 1 ? '1 Working Day' : `${days} Working Days`);
      }
    }
    
    return {
      id: service.id,
      title: isAr ? service.title_ar : service.title_en,
      icon: getServiceIcon(service.icon),
      iconName: service.icon,
      description: isAr ? service.description_ar : service.description_en,
      price: displayPrice,
      priceRange: priceRange,
      priceType: service.price_type || 'fixed',
      processingTime: processingTime,
      category: service.category || service.service_type
    };
  });

  // Build filters from categories
  const filters = [
    { id: 'all', label: isAr ? 'جميع الخدمات' : 'All Services', count: categories.find(c => c.id === 'all')?.count || services.length },
    { id: 'visa', label: isAr ? 'خدمات التأشيرة' : 'Visa Services', count: categories.find(c => c.id === 'visa')?.count || 0 },
    { id: 'emirates_id', label: isAr ? 'الهوية الإماراتية' : 'Emirates ID', count: categories.find(c => c.id === 'emirates_id')?.count || 0 },
    { id: 'renewal', label: isAr ? 'التجديدات' : 'Renewals', count: categories.find(c => c.id === 'renewal')?.count || 0 },
    { id: 'medical', label: isAr ? 'الطبية' : 'Medical', count: categories.find(c => c.id === 'medical')?.count || 0 },
    { id: 'status_change', label: isAr ? 'تغيير الحالة' : 'Status Change', count: categories.find(c => c.id === 'status_change')?.count || 0 }
  ].filter(f => f.id === 'all' || f.count > 0); // Only show filters with services

  const nationalities = [
    'إماراتي', 'سعودي', 'كويتي', 'قطري', 'بحريني', 'عماني', 'أردني', 'مصري', 'سوري', 'لبناني', 
    'فلسطيني', 'عراقي', 'يمني', 'سوداني', 'مغربي', 'تونسي', 'جزائري', 'ليبي', 'هندي', 'باكستاني',
    'بنغلاديشي', 'فلبيني', 'إندونيسي', 'سريلانكي', 'نيبالي', 'صيني', 'بريطاني', 'أمريكي', 'فرنسي', 'ألماني', 'أخرى'
  ];

  // Filter services based on active filter
  const filteredServices = activeFilter === 'all' 
    ? displayServices 
    : displayServices.filter(s => s.category === activeFilter);

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(isAr ? 'حجم الملف يجب أن يكون أقل من 5 ميجابايت' : 'File size must be less than 5MB');
        return;
      }
      setUploadedFiles(prev => ({ ...prev, [field]: file }));
      toast.success(isAr ? `تم رفع ${file.name} بنجاح` : `${file.name} uploaded successfully`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }

    // Validate required fields
    if (!formData.full_name_ar || !formData.full_name_en || !formData.nationality || !formData.passport_number) {
      toast.error(isAr ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Create form data for file upload
      const submitData = new FormData();
      submitData.append('service_type', 'visa');
      submitData.append('title', `طلب ${selectedService?.title || 'خدمة تأشيرة'}`);
      submitData.append('description', JSON.stringify({
        service_id: selectedService?.id,
        service_name: selectedService?.title,
        applicant_details: formData,
        price: selectedService?.price
      }));
      submitData.append('contact_name', formData.full_name_en);
      submitData.append('contact_email', formData.email);
      submitData.append('contact_phone', formData.phone);

      // Note: File upload would need backend support
      // For now, we send the form data without files
      
      await axios.post(
        `${BACKEND_URL}/api/services/request`,
        {
          service_type: 'visa',
          title: `طلب ${selectedService?.title || 'خدمة تأشيرة'}`,
          description: JSON.stringify({
            service_id: selectedService?.id,
            service_name: selectedService?.title,
            applicant_details: formData,
            price: selectedService?.price,
            files_uploaded: Object.keys(uploadedFiles).filter(k => uploadedFiles[k])
          }),
          contact_name: formData.full_name_en,
          contact_email: formData.email,
          contact_phone: formData.phone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('تم إرسال طلبك بنجاح! سيتواصل معك فريقنا قريباً');
      setSelectedService(null);
      setFormData({
        service_type: 'visa',
        full_name_ar: '',
        full_name_en: user?.name || '',
        nationality: '',
        passport_number: '',
        passport_expiry: '',
        current_visa_status: '',
        phone: user?.phone || '',
        email: user?.email || '',
        date_of_birth: '',
        gender: '',
        marital_status: '',
        sponsor_name: '',
        sponsor_uid: '',
        notes: ''
      });
      setUploadedFiles({ passport: null, photo: null, emiratesId: null, other: null });
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isAr ? 'حدث خطأ في إرسال الطلب' : 'Error submitting request'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero Section - Compact */}
      <section className={`py-6 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/services/documents')}
            className={`flex items-center gap-2 mb-4 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-white/80 hover:text-white'}`}
            data-testid="back-button"
          >
            {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            {isAr ? 'العودة' : 'Back'}
          </button>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-white/20'}`}>
              <CreditCard className={`h-8 w-8 ${isDarkMode ? 'text-blue-400' : 'text-white'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>{t('visa.title')}</h1>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-white/80'}`}>
                {isAr ? 'خدمات التأشيرات والهوية الإماراتية' : 'Visa & Emirates ID Services'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className={`py-4 border-b ${isDarkMode ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                data-testid={`filter-${filter.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeFilter === filter.id
                    ? 'bg-[#3B82F6] text-white shadow-lg'
                    : isDarkMode ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className={`py-8 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {servicesLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-[#3B82F6]" />
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-20">
              <CreditCard className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                {isAr ? 'لا توجد خدمات في هذه الفئة' : 'No services in this category'}
              </p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredServices.map((service) => (
              <div 
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className={`rounded-xl p-5 transition-all duration-300 flex flex-col ${isDarkMode ? 'bg-[#141414] border border-white/5 hover:border-blue-500/30' : 'bg-white border-2 border-gray-200 shadow-sm hover:shadow-lg hover:border-blue-500/30'}`}
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${isDarkMode ? 'bg-blue-500/10' : 'bg-[#EFF6FF]'}`}>
                  <div className="text-[#3B82F6]">
                    {service.icon}
                  </div>
                </div>
                
                {/* Title */}
                <h3 className={`text-base font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{service.title}</h3>
                
                {/* Description */}
                <p className={`text-xs mb-3 flex-grow ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>{service.description}</p>
                
                {/* Price */}
                <div className="mb-3">
                  {service.priceType === 'range' && service.priceRange ? (
                    <div className="text-xl font-bold text-[#3B82F6]">
                      <PriceDisplay price={service.priceRange.min} size="lg" />
                      <span className="mx-1">-</span>
                      <PriceDisplay price={service.priceRange.max} size="lg" />
                    </div>
                  ) : (
                    <PriceDisplay price={service.price} className="text-xl font-bold text-[#3B82F6]" size="lg" />
                  )}
                </div>
                
                {/* Processing Time */}
                <div className={`flex items-center gap-2 text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-[#64748B]'}`}>
                  <Clock className="h-3 w-3" />
                  <span>{service.processingTime}</span>
                </div>
                
                {/* Button - Navigate to Service Detail Page */}
                <Button 
                  onClick={() => {
                    navigate(`/services/visa/${service.id}`);
                  }}
                  data-testid={`start-service-${service.id}`}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm py-2"
                >
                  {isAr ? 'ابدأ الطلب' : 'Start Request'}
                </Button>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>
      {/* Service Info Modal */}
      {selectedService && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className={`rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto my-4 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white shadow-2xl'}`}>
            {/* Modal Header - Service Info */}
            <div className="bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] rounded-t-2xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white/20`}>
                  {selectedService.icon}
                </div>
                <button 
                  onClick={() => setSelectedService(null)}
                  data-testid="close-modal"
                  className="text-white/70 hover:text-white text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
              <h3 className="text-xl font-bold mb-1">{selectedService.title}</h3>
              <p className="text-white/80 text-sm mb-4">{selectedService.description}</p>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-xs mb-1">{isAr ? 'السعر' : 'Price'}</p>
                  {selectedService.priceType === 'range' && selectedService.priceRange ? (
                    <div className="font-bold text-xl">
                      {selectedService.priceRange.min.toLocaleString()} - {selectedService.priceRange.max.toLocaleString()} {isAr ? 'د.إ' : 'AED'}
                    </div>
                  ) : (
                    <div className="font-bold text-xl">{selectedService.price?.toLocaleString()} {isAr ? 'د.إ' : 'AED'}</div>
                  )}
                </div>
                <div className="text-end">
                  <p className="text-white/60 text-xs mb-1">{isAr ? 'مدة المعالجة' : 'Processing Time'}</p>
                  <div className="flex items-center gap-1.5 font-medium">
                    <Clock className="h-4 w-4" />
                    {selectedService.processingTime}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              {/* User Info Section */}
              {user ? (
                <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <User className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                        {isAr ? 'بياناتك المسجلة' : 'Your Profile Info'}
                      </h4>
                      <p className={`text-xs ${isDarkMode ? 'text-green-400/70' : 'text-green-600'}`}>
                        {isAr ? 'سيتم استخدامها في الطلب' : 'Will be used in the request'}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <User className="h-4 w-4 text-gray-400" />
                      <span>{user.name || '-'}</span>
                    </div>
                    <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span dir="ltr">{user.email || '-'}</span>
                    </div>
                    {user.phone && (
                      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span dir="ltr">{user.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                      <User className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-sm ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                        {isAr ? 'يجب تسجيل الدخول' : 'Login Required'}
                      </h4>
                      <p className={`text-xs ${isDarkMode ? 'text-red-400/70' : 'text-red-600'}`}>
                        {isAr ? 'سجل دخولك للمتابعة في تقديم الطلب' : 'Please login to continue'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Required Documents Section */}
              {loadingDocs ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-[#3B82F6]" />
                  <span className={`mr-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isAr ? 'جاري تحميل الأوراق المطلوبة...' : 'Loading required documents...'}
                  </span>
                </div>
              ) : requiredDocuments.length > 0 ? (
                <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                      <FileText className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                        {isAr ? 'الأوراق المطلوبة' : 'Required Documents'}
                      </h4>
                      <p className={`text-xs ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
                        {isAr ? 'تأكد من توفرها قبل التقديم' : 'Make sure you have them ready'}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {requiredDocuments.map((doc, index) => (
                      <li key={doc.id || index} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${doc.is_required ? 'text-amber-500' : 'text-green-500'}`} />
                        <span>
                          {isAr ? doc.name_ar : doc.name_en}
                          {doc.is_required && <span className="text-red-500 mr-1">*</span>}
                          {doc.nationality_specific && (
                            <span className={`text-xs mr-2 px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                              {isAr ? 'حسب الجنسية' : 'By nationality'}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-500/10 border border-gray-500/20' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-500/20' : 'bg-gray-100'}`}>
                      <FileText className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {isAr ? 'الأوراق المطلوبة' : 'Required Documents'}
                      </h4>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {isAr ? 'سيتم تحديدها مع المساعد الذكي' : 'Will be determined with AI assistant'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                {user ? (
                  <Button 
                    onClick={() => {
                      // Navigate to AI chat with service context
                      navigate(`/services/chat?service_id=${selectedService.id}&service_name=${encodeURIComponent(selectedService.title)}`);
                    }}
                    data-testid="start-ai-chat"
                    className="w-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:from-[#2563EB] hover:to-[#7C3AED] text-white py-6 text-base font-bold rounded-xl flex items-center justify-center gap-2"
                  >
                    <Sparkles className="h-5 w-5" />
                    {isAr ? 'التحدث مع المساعد الذكي' : 'Talk to AI Assistant'}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/login')}
                    data-testid="login-button"
                    className="w-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white py-6 text-base font-bold rounded-xl flex items-center justify-center gap-2"
                  >
                    <User className="h-5 w-5" />
                    {isAr ? 'تسجيل الدخول للمتابعة' : 'Login to Continue'}
                  </Button>
                )}
                
                <button 
                  onClick={() => setSelectedService(null)}
                  className={`w-full py-3 text-sm font-medium rounded-xl transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  {isAr ? 'إغلاق' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

